import React, { useCallback, useEffect, useState } from "react";
import type {
  Appointment,
  PendingPaymentAppointment,
  PendingPaymentsByYearData,
} from "../../../types";
import { ServiceType } from "../../../types";
import { adminApi } from "../../../services";
import { getTodayMonthYearBrazil } from "../../../utils/dateUtils";
import { AdminPageHeader } from "../shared";
import { Card, Icons } from "../../shared";
import { UpdatePaymentModal } from "../AdminAppointments/UpdatePaymentModal";
import { PendingPaymentsPanel } from "./PendingPaymentsPanel";
import { formatBalanceCurrency } from "./balanceShared";

function unpaidPriceString(price: number | string): string {
  if (typeof price === "number") return String(price);
  return price;
}

function mapUnpaidToAppointment(
  row: PendingPaymentAppointment
): Appointment {
  const priceStr = unpaidPriceString(row.price);
  return {
    id: row.id,
    clientId: row.client.id,
    client: {
      id: row.client.id,
      name: row.client.name,
      phoneNumber: row.client.phoneNumber,
    },
    professionalId: 0,
    serviceId: row.serviceId,
    appointmentDate: row.appointmentDate,
    startTime: row.startTime ?? "",
    endTime: row.endTime ?? "",
    price: priceStr,
    status: row.status,
    payment: null,
    isRecurrency: false,
    updatedAt: "",
    createdAt: "",
    service: {
      name: row.service?.name ?? "Serviço",
      description: row.service?.description ?? "",
      duration: 0,
      price: priceStr,
      type: ServiceType.TIME,
      companyId: 0,
    },
    progressSteps: [],
  };
}

export const AdminPendingPaymentsPage: React.FC = () => {
  const [year, setYear] = useState(() => getTodayMonthYearBrazil().year);
  const [payload, setPayload] = useState<PendingPaymentsByYearData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [appointmentForPayment, setAppointmentForPayment] =
    useState<Appointment | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getPendingPaymentsByYear(year);
      if (res.success && res.data) {
        setPayload(res.data);
      } else {
        setPayload(null);
        setError(res.error || "Erro ao carregar pagamentos pendentes");
      }
    } catch (e) {
      setPayload(null);
      setError(
        e instanceof Error ? e.message : "Erro ao carregar pagamentos pendentes"
      );
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const clients = payload?.clients ?? [];
  const total = payload?.total ?? 0;
  const count = payload?.count ?? 0;

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Payment sx={{ fontSize: 28 }} />}
        title="Pagamentos Pendentes"
        subtitle={
          loading
            ? `Carregando pendências de ${year}…`
            : `${count} ${count === 1 ? "agendamento sem pagamento" : "agendamentos sem pagamento"} em ${year}.`
        }
      />

      <div className="flex items-center justify-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => setYear((y) => y - 1)}
          className="flex-shrink-0 p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="Ano anterior"
        >
          <Icons.ChevronLeft sx={{ fontSize: 24 }} />
        </button>
        <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white min-w-[4rem] text-center">
          {year}
        </span>
        <button
          type="button"
          onClick={() => setYear((y) => y + 1)}
          className="flex-shrink-0 p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="Próximo ano"
        >
          <Icons.ChevronRight sx={{ fontSize: 24 }} />
        </button>
      </div>

      {!loading && !error && (
        <Card className="p-4 sm:p-6 border border-gray-100 dark:border-slate-700 shadow-sm">
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">
            Total pendente em {year}
          </p>
          <p className="text-2xl sm:text-3xl font-bold text-red-700 dark:text-red-400">
            {formatBalanceCurrency(total)}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
            {clients.length}{" "}
            {clients.length === 1 ? "cliente" : "clientes"} com pagamento em aberto
          </p>
        </Card>
      )}

      <PendingPaymentsPanel
        clients={clients}
        loading={loading}
        error={error}
        onRetry={loadPending}
        onPay={(appointmentId, group) => {
          const row = group.appointments.find((a) => a.id === appointmentId);
          if (!row) return;
          setAppointmentForPayment(mapUnpaidToAppointment(row));
          setPaymentModalOpen(true);
        }}
      />

      {appointmentForPayment && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          onSuccess={(updatedAppointment) => {
            if (updatedAppointment) {
              loadPending();
            }
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          appointment={appointmentForPayment}
        />
      )}
    </div>
  );
};
